import type { UserInputs, GeneratedName } from '../types';
import brandData from '../brand_name_database.json';

// This file uses a local database and algorithms, not the Gemini API.

function needsSpace(keyword: string): boolean {
    if (!keyword) return false;
    // Treat very short words (≤3 letters) or all-caps acronyms as needing a space
    const shortWord = keyword.length <= 3;
    const allCapsAcronym = /^[A-Z]{2,}$/.test(keyword);
    return shortWord || allCapsAcronym;
}

// Selects a random item from an array.
function randomChoice(array: string[]): string {
    if (!array || array.length === 0) return '';
    return array[Math.floor(Math.random() * array.length)];
}

// Shortens a keyword to a more brandable, partial word.
function shortenKeyword(keyword: string): string {
    if (!keyword) return '';
    const clean = keyword.replace(/[^a-zA-Z\s]/g, ''); // Allow spaces for multi-word keywords
    if (clean.length <= 5) return clean.split(' ')[0]; // Take first word if short
    return clean.slice(0, 4 + Math.floor(Math.random() * 3));
}

// Blends two words together intelligently.
function blend(part1: string, part2: string): string {
    if (!part1) return (part2 || '').toLowerCase();
    if (!part2) return part1.toLowerCase();
    const p1 = part1.toLowerCase();
    const p2 = part2.toLowerCase();
    const minOverlap = 1;
    const maxOverlap = Math.min(p1.length, p2.length) - 1;

    for (let i = maxOverlap; i >= minOverlap; i--) {
        if (p1.slice(-i) === p2.slice(0, i)) return p1 + p2.slice(i);
    }

    const vowels = "aeiou";
    if (vowels.includes(p1.slice(-1)) && p2.startsWith(p1.slice(-1))) return p1 + p2.slice(1);
    return p1 + p2;
}

// Applies stylistic phonetic rules to a word.
function applyPhoneticRules(word: string, rules: any): string {
    if (!word) return '';
    let w = word.toLowerCase();

    if (rules.replace_letters && Math.random() < 0.25) {
        for (const [from, to] of Object.entries(rules.replace_letters)) {
            try {
                const regex = new RegExp(from, 'g');
                if (regex.test(w)) { w = w.replace(regex, to as string); break; }
            } catch (e) { console.warn(`Invalid regex: ${from}`); }
        }
    }

    if (rules.drop_vowels && Math.random() < 0.08) {
        const vowels = 'aeiou';
        const vowelIndices: number[] = [];
        for (let i = 1; i < w.length - 1; i++) {
            if (vowels.includes(w[i]) && !vowels.includes(w[i - 1]) && !vowels.includes(w[i + 1])) vowelIndices.push(i);
        }
        if (vowelIndices.length > 0) {
            const idx = vowelIndices[Math.floor(Math.random() * vowelIndices.length)];
            const potential = w.slice(0, idx) + w.slice(idx + 1);
            if (!/[bcdfghjklmnpqrstvwxyz]{3,}/i.test(potential)) w = potential;
        }
    }

    return w.split(' ').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

// Generates a single creative brand name.
function generateSingleBrand(inputs: UserInputs, data: any): string {
    const styleWords = data.style_map?.[inputs.style] || [];
    const stopWords = new Set(['a', 'an', 'the', 'for', 'of', 'with', 'and', 'or', 'is', 'in']);

    let relevantKeywords: string[] = [];
    
    let matchedSubIndustry: string | null = null;
    const combinedSearchTerms = `${inputs.industry || ''} ${inputs.keywords || ''}`
        .toLowerCase()
        .split(/[, _-]+/)
        .filter(Boolean);

    matchedSubIndustry = Object.keys(data.industries)
        .find(key => combinedSearchTerms.includes(key.toLowerCase())) || null;

    if (inputs.industry && data.industries[inputs.industry.toLowerCase().trim()]) {
        relevantKeywords.push(...data.industries[inputs.industry.toLowerCase().trim()]);
    } else if (matchedSubIndustry) {
        relevantKeywords.push(...data.industries[matchedSubIndustry]);
    } else if (inputs.industry) {
        relevantKeywords.push(...inputs.industry.split(/[, ]+/));
    }

    if (inputs.keywords) relevantKeywords.push(...inputs.keywords.split(/[, ]+/));

    relevantKeywords = relevantKeywords.map(k => k.trim()).filter(kw => kw.length > 2 && !stopWords.has(kw.toLowerCase()));
    if (relevantKeywords.length === 0) {
        const industryKeywordLists = Object.values(data.industries) as string[][] || [];
        if (industryKeywordLists.length > 0) {
            const randomList = industryKeywordLists[Math.floor(Math.random() * industryKeywordLists.length)];
            relevantKeywords.push(...randomList);
        }
    }

    const keyword = randomChoice(relevantKeywords);
    const shortKeyword = shortenKeyword(keyword);
    const prefix = randomChoice(data.prefixes);
    const suffix = randomChoice(data.suffixes);
    const benefit = randomChoice(data.benefits || []);
    const styleWord = randomChoice(styleWords);

    const patterns = [
        needsSpace(shortKeyword) ? `${prefix} ${shortKeyword}` : blend(prefix, shortKeyword),
        needsSpace(shortKeyword) ? `${prefix} ${shortKeyword}` : prefix + shortKeyword,
        shortKeyword + suffix,
        blend(shortKeyword, suffix),
        `${keyword} ${benefit}`,
        `${keyword} ${styleWord}`,
        `${prefix} ${keyword}`
    ];

    let name = randomChoice(patterns);
    name = applyPhoneticRules(name, data.phonetic_rules);

    if (data.bad_endings) {
        for (const bad of data.bad_endings) {
            if (name.toLowerCase().endsWith(bad)) {
                name = name.slice(0, -bad.length) + randomChoice(data.suffixes);
            }
        }
    }

    return name;
}

// Generates a keyword-heavy name to satisfy strong keyword input.
function generateKeywordHeavyName(inputs: UserInputs, data: any): string {
    if (!inputs.keywords) return generateSingleBrand(inputs, data);

    let keywordWords = inputs.keywords.split(/[, ]+/).filter(k => k.length > 2);
    
    let matchedSubIndustry: string | null = null;
    if (inputs.keywords) {
        const userKeywords = inputs.keywords.toLowerCase().split(/[, ]+/);
        matchedSubIndustry = Object.keys(data.industries)
            .find(key => userKeywords.includes(key.toLowerCase())) || null;
    }
    if (matchedSubIndustry) {
        keywordWords = [...data.industries[matchedSubIndustry]];
    }

    const keyword = randomChoice(keywordWords);
    const prefix = randomChoice(data.prefixes);
    const suffix = randomChoice(data.suffixes);
    const benefit = randomChoice(data.benefits || []);

    const patterns = [
        needsSpace(keyword) ? `${prefix} ${keyword}` : keyword + suffix,
        needsSpace(keyword) ? `${prefix} ${keyword}` : prefix + keyword,
        `${benefit} ${keyword}`
    ];

    return randomChoice(patterns);
}

// Main function to generate brand names.
export const generateBrandNames = (inputs: UserInputs): GeneratedName[] => {
    const generated = new Set<string>();
    let attempts = 0;

    const lengthRanges = {
        short: { min: 4, max: 6 },
        medium: { min: 7, max: 10 },
        long: { min: 11, max: 16 },
    };
    const targetRange = lengthRanges[inputs.length?.toLowerCase() as keyof typeof lengthRanges] || { min: 4, max: 16 };

    const hasKeywords = inputs.keywords && inputs.keywords.trim().length > 0;
    let keywordHeavyCount = 0;

    while (generated.size < 5 && attempts < 500) {
        let name = '';

        if (hasKeywords && keywordHeavyCount < 4) {
            name = generateKeywordHeavyName(inputs, brandData);
            keywordHeavyCount++;
        } else {
            name = generateSingleBrand(inputs, brandData);
        }

        if (name.length >= targetRange.min && name.length <= targetRange.max) {
            generated.add(name);
        }

        attempts++;
    }

    return Array.from(generated).slice(0, 5).map(name => ({ name }));
};